<template>
  <div>
    <select-field v-model="fieldValue" :schema="formattedSchema">
    </select-field>
    <v-dialog v-model="showCustom" max-width="480px">
      <modal-card title="Custom Timeframe" fixed @close="showCustom = false">
        <div class="pa-4">
          <field-block title="Start Time" class="mb-2">
            <date-time-field :schema="startTimeSchema" v-model="startTime">
            </date-time-field>
          </field-block>
          <field-block title="End Time" class="mb-2">
            <date-time-field :schema="startTimeSchema" v-model="endTime">
            </date-time-field>
          </field-block>
        </div>
        <template v-slot:actions>
          <v-btn color="appMain" @click="setCustomTime" :disabled="!canUpdate">
            Update
          </v-btn>
        </template>
      </modal-card>
    </v-dialog>
  </div>
</template>
<script>
import SelectField from '@/components/formFields/SelectField';
import DateTimeField from '@/components/formFields/DateTimeField';

import clone from 'lodash/clone';
import {
  startOfDay,
  endOfDay,
  startOfWeek,
  subDays,
  endOfWeek,
  addDays,
  format,
} from 'date-fns';

export default {
  name: 'ReportTimeframe',
  components: {
    SelectField,
    DateTimeField,
  },
  props: {
    schema: {
      type: Object,
      default: () => ({}),
    },
    //the value of the timeframe param NOT the start and end times, which are controls by parsing the timeframe value itself
    value: {},
  },
  data() {
    const startDate = startOfDay(new Date());
    const dateFormat = 'yyyy-MM-dd HH:mm';
    const schema = this.schema || {};

    return {
      customOptions: [],
      showCustom: false,
      startTime: format(startDate, dateFormat),
      endTime: format(endOfDay(startDate), dateFormat),
      options: [
        {
          value: 'h1',
          label: 'Last Hour',
        },
        {
          value: 'h2',
          label: 'Last 2 Hours',
        },
        {
          value: 'h3',
          label: 'Last 3 Hours',
        },
        {
          value: 'h4',
          label: 'Last 4 Hours',
        },
        {
          value: 'h24',
          label: 'Last 24 Hours',
        },
        {
          value: 'today',
          label: 'Today',
        },
        {
          value: 'todayToNoon',
          label: 'Today Until Noon',
        },
        {
          value: 'tabWeek',
          label: 'Tab Week',
        },
        {
          value: 'lastTabWeek',
          label: 'Last Tab Week',
        },
        {
          value: '7d',
          label: 'Next 7 Days',
        },
        {
          value: 'custom',
          label: 'Add Custom',
        },
      ],
      startTimeSchema: {
        label: 'Start Time',
        min: schema.start_min || null,
        max: schema.start_max || null,
      },
      endTimeSchema: {
        title: 'End Time',
        min: schema.end_min || null,
        max: schema.end_max || null,
      },
    };
  },
  computed: {
    fieldValue: {
      get() {
        return this.value || this.schema.default || '';
      },
      set(value) {
        this.$emit('input', value);
      },
    },

    formattedSchema() {
      const schema = clone(this.schema);
      const options = clone(this.options);
      if (schema.all_time) {
        options.unshift({
          label: 'All Time',
          value: 'all_time',
        });
      }

      schema.options = options.concat(this.customOptions).map((option) => {
        return {
          value: option.value,
          text: option.label || option.text,
        };
      });
      return schema;
    },

    canUpdate() {
      return this.startTime;
    },
  },
  watch: {
    fieldValue: {
      immediate: true,
      handler: function (newValue) {
        //without next tick here, this fires
        //before the parent can receive it.
        //this shows up when you route directly to the report from reports index
        //whereas if you load the report directly without params, start_time and end_time load immediatley
        this.$nextTick(() => {
          this.setStartAndEndTime(newValue);
        });
      },
    },
  },
  methods: {
    milliToSeconds(time) {
      return Math.floor(time / 1000);
    },

    getWeek(prev = false) {
      let weekStart = startOfWeek(new Date(), { weekStartsOn: 2 });
      if (prev) {
        weekStart = subDays(weekStart, 7);
      }

      const weekEnd = endOfWeek(weekStart, { weekStartsOn: 2 });

      return {
        weekStart: weekStart.getTime(),
        weekEnd: weekEnd.getTime(),
      };
    },

    setStartAndEndTime(value) {
      let dateNow = new Date();
      let timeNow = dateNow.getTime();

      let startTime = timeNow;
      let endTime = timeNow;

      if (value === 'custom') {
        this.showCustom = true;
        return;
      }

      const milli = (value) => value * 1000;

      if (value.includes('-')) {
        const timeRangeArr = value.split('-');
        startTime = milli(timeRangeArr[0]);
        if (timeRangeArr[1]) {
          endTime = milli(timeRangeArr[1]);
        }
      }

      //if minutes code
      if (value.charAt(0) === 'm') {
        const min = value.substring(1);
        startTime = startTime - milli(min * 60);
      }

      //hours
      if (value.charAt(0) === 'h') {
        const hour = value.substring(1);
        startTime = startTime - milli(hour * 60 * 60);
      }

      if (value.includes('today')) {
        startTime = startOfDay(dateNow).getTime();
        if (value === 'todayToNoon') {
          endTime = startTime + milli(12 * 60 * 60);
        } else {
          endTime = endOfDay(dateNow).getTime();
        }
      }

      if (value.includes('Week')) {
        const week = this.getWeek(value.includes('last'));
        startTime = week.weekStart;
        endTime = week.weekEnd;
      }

      if (value === 'all_time') {
        const today = startOfDay(timeNow);
        startTime = subDays(today, 365);
        endTime = addDays(today, 90);
      }

      if (value === '7d') {
        endTime = addDays(timeNow, 7);
      }

      const params = {
        start_time: this.milliToSeconds(startTime),
        end_time: this.milliToSeconds(endTime),
      };

      for (const param in params) {
        this.$emit('input', {
          param,
          value: params[param],
        });
      }
    },

    setCustomTime() {
      const labels = [];
      const values = [];

      const addTimestamp = (stringDate) => {
        if (!stringDate) {
          return;
        }

        try {
          const d = new Date(stringDate);
          labels.push(format(d, 'MMM do, h:mm aaa'));
          values.push(this.milliToSeconds(d.getTime()));
        } catch (e) {
          return;
        }
      };

      addTimestamp(this.startTime);
      addTimestamp(this.endTime);

      if (!values.length) {
        return;
      }

      const value = values.join('-');
      this.customOptions.push({
        label: labels.join(' - '),
        value,
      });

      //will auto set timeframe value and set start/endtimes
      this.fieldValue = value;

      this.showCustom = false;
    },
  },
};
</script>
